import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { History, ArrowUp, RefreshCw, Receipt, Undo2, SlidersHorizontal, Crown, XCircle, Clock, Loader2 } from "lucide-react";
import { format } from "date-fns";
import {
  PAYMENT_TRANSACTION_TYPES,
  useWorkspaceBilling,
  isIndiaCard,
  formatLandsBy,
} from "@/hooks/use-workspace-billing";
import { formatMoney } from "@/lib/money";
import { paymentRow, type PaymentRow, type PaymentTransaction } from "./payment-row";

interface UsageHistoryProps {
  transactions: PaymentTransaction[];
  isLoading: boolean;
  isError?: boolean;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

const RED = { icon: "text-red-600 dark:text-red-400", bg: "bg-red-100 dark:bg-red-950/50", badge: "bg-red-50 text-red-700 border-red-200 dark:bg-red-950/50 dark:text-red-300 dark:border-red-800" };

/** How each payment type is drawn: its icon, the icon's tile and the badge. */
const LOOK: Record<string, { label: string; Icon: typeof History; icon: string; bg: string; badge: string }> = {
  top_up: { label: "Top-up", Icon: ArrowUp, icon: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-100 dark:bg-emerald-950/50", badge: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-300 dark:border-emerald-800" },
  auto_reload: { label: "Auto-reload", Icon: RefreshCw, icon: "text-blue-600 dark:text-blue-400", bg: "bg-blue-100 dark:bg-blue-950/50", badge: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/50 dark:text-blue-300 dark:border-blue-800" },
  refund: { label: "Refund", Icon: Undo2, ...RED },
  adjustment: { label: "Adjustment", Icon: SlidersHorizontal, icon: "text-amber-600 dark:text-amber-400", bg: "bg-amber-100 dark:bg-amber-950/50", badge: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/50 dark:text-amber-300 dark:border-amber-800" },
  subscription_charge: { label: "Pro subscription", Icon: Crown, icon: "text-violet-600 dark:text-violet-400", bg: "bg-violet-100 dark:bg-violet-950/50", badge: "bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/50 dark:text-violet-300 dark:border-violet-800" },
  top_up_failed: { label: "Failed", Icon: XCircle, ...RED },
  subscription_refund: { label: "Pro refund", Icon: Undo2, icon: "text-violet-600 dark:text-violet-400", bg: "bg-violet-100 dark:bg-violet-950/50", badge: "bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/50 dark:text-violet-300 dark:border-violet-800" },
};

const TONE: Record<PaymentRow["tone"], string> = {
  credit: "text-emerald-600 dark:text-emerald-400",
  debit: "text-red-600 dark:text-red-400",
  failed: "text-red-600 dark:text-red-400",
  card: "text-foreground",
};

export function UsageHistory({
  transactions,
  isLoading,
  isError,
  hasMore,
  isLoadingMore,
  onLoadMore,
}: UsageHistoryProps) {
  // Payment History only covers wallet funding. The query already filters to
  // PAYMENT_TRANSACTION_TYPES; this guards against a caller passing usage rows.
  const filteredTransactions = transactions.filter(t =>
    (PAYMENT_TRANSACTION_TYPES as string[]).includes(t.transaction_type)
  );

  // A reload the bank is holding shows as one synthetic "Scheduled" row on top:
  // no money has moved yet, so it carries no balance.
  const { data: billing } = useWorkspaceBilling();
  const scheduled = billing?.auto_reload_pending
    ? {
        amount: formatMoney(billing.auto_reload_pending_amount_credits ?? billing.auto_reload_amount_credits),
        landsBy: formatLandsBy(billing.auto_reload_pending_completes_at, isIndiaCard(billing)),
      }
    : null;

  if (isLoading) {
    return (
      <Card className="border-muted/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Payment History
          </CardTitle>
          <CardDescription>Your wallet top-ups, refunds and subscription charges</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 rounded-xl bg-muted animate-pulse" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-muted/50">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-lg font-semibold">
          <div className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center">
            <History className="w-4 h-4 text-muted-foreground" />
          </div>
          Payment History
        </CardTitle>
        <CardDescription className="text-base">
          Your wallet top-ups, refunds and subscription charges
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isError ? (
          <div className="text-center py-16">
            <p className="text-muted-foreground font-medium text-lg">
              Couldn't load your payment history
            </p>
            <p className="text-sm text-muted-foreground mt-1.5">
              Please refresh the page to try again.
            </p>
          </div>
        ) : filteredTransactions.length === 0 && !scheduled ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 rounded-2xl bg-muted/50 mx-auto mb-5 flex items-center justify-center">
              <Receipt className="w-10 h-10 text-muted-foreground/50" />
            </div>
            <p className="text-muted-foreground font-medium text-lg">No transactions yet</p>
            <p className="text-sm text-muted-foreground mt-1.5 max-w-sm mx-auto">
              Your top-ups and other wallet payments will appear here once you add credits to your wallet
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {scheduled && (
              <div className="flex items-center justify-between p-4 rounded-xl border border-dashed border-border/70 bg-card">
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-xl bg-blue-100 dark:bg-blue-950/50 flex items-center justify-center">
                    <Clock className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2.5">
                      <span className="font-medium">
                        Auto-reload scheduled: {scheduled.amount} — completes after {scheduled.landsBy}
                      </span>
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/50 dark:text-blue-300 dark:border-blue-800">
                        Scheduled
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      Requested from your bank; it can't be cancelled
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xl font-semibold text-muted-foreground">{scheduled.amount}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Not yet credited</p>
                </div>
              </div>
            )}
            {filteredTransactions.map((transaction) => {
              const row = paymentRow(transaction);
              const look = LOOK[transaction.transaction_type];
              return (
                <div
                  key={transaction.id}
                  className="flex items-center justify-between p-4 rounded-xl border border-border/50 bg-card hover:bg-accent/30 hover:border-border transition-all duration-200"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-11 h-11 rounded-xl ${look.bg} flex items-center justify-center`}>
                      <look.Icon className={`w-4 h-4 ${look.icon}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2.5">
                        <span className="font-medium">{row.title}</span>
                        <Badge variant="outline" className={look.badge}>{look.label}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        {row.reason ? `${row.reason} · ` : ""}
                        {format(new Date(transaction.created_at), "MMM d, yyyy 'at' h:mm a")}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-xl font-semibold ${TONE[row.tone]}`}>{row.amount}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{row.note}</p>
                  </div>
                </div>
              );
            })}
            {hasMore && (
              <Button
                variant="outline"
                className="w-full"
                onClick={onLoadMore}
                disabled={isLoadingMore}
              >
                {isLoadingMore && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {isLoadingMore ? "Loading…" : "Load more"}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
